import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { OwnerPicker } from '@/components/extraction/OwnerPicker'

interface TeamMember {
  id: string
  userId: string
  fullName: string | null
  avatarUrl: string | null
}

export type FieldType =
  | 'text'
  | 'textarea'
  | 'select'
  | 'owner'
  | 'checkbox'
  | 'date'
  | 'time'

export interface SelectOption {
  value: string
  label: string
}

export interface ModalFieldConfig {
  key: string
  label: string
  type: FieldType
  placeholder?: string
  required?: boolean
  options?: ReadonlyArray<string | SelectOption>
  defaultValue?: string | boolean
  fullWidth?: boolean
}

export interface AddItemModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  fields: ModalFieldConfig[]
  onSubmit: (values: Record<string, string | boolean>) => void | Promise<void>
  teamMembers?: TeamMember[]
  submitLabel?: string
}

function normalizeOptions(
  options: ReadonlyArray<string | SelectOption>
): SelectOption[] {
  return options.map((opt) =>
    typeof opt === 'string' ? { value: opt, label: opt } : opt
  )
}

export function AddItemModal({
  open,
  onOpenChange,
  title,
  description,
  fields,
  onSubmit,
  teamMembers = [],
  submitLabel = 'Add',
}: AddItemModalProps) {
  const [values, setValues] = useState<Record<string, string | boolean>>({})
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (open) {
      const initial: Record<string, string | boolean> = {}
      for (const field of fields) {
        initial[field.key] =
          field.defaultValue ?? (field.type === 'checkbox' ? false : '')
      }
      setValues(initial)
    }
  }, [open, fields])

  const setField = (key: string, value: string | boolean) => {
    setValues((prev) => ({ ...prev, [key]: value }))
  }

  const requiredOk = fields
    .filter((f) => f.required)
    .every((f) => {
      const v = values[f.key]
      return typeof v === 'string' ? v.trim().length > 0 : !!v
    })

  const handleSubmit = async () => {
    if (!requiredOk || isSaving) return
    setIsSaving(true)
    try {
      await onSubmit(values)
      onOpenChange(false)
    } catch {
      // The caller reports the failure (it knows what the row is called). Keep
      // the dialog open with the typed values so the retry costs nothing, and
      // swallow here rather than rejecting into `void handleSubmit()`.
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 py-2">
          {fields.map((field) => {
            const value = values[field.key]
            const stringValue = typeof value === 'string' ? value : ''
            const boolValue = typeof value === 'boolean' ? value : false
            const colSpan = field.fullWidth || field.type === 'textarea' ? 'col-span-2' : 'col-span-2 sm:col-span-1'

            return (
              <div key={field.key} className={`${colSpan} space-y-1.5`}>
                <Label htmlFor={`field-${field.key}`} className="text-xs">
                  {field.label}
                  {field.required && <span className="text-destructive ml-0.5">*</span>}
                </Label>

                {field.type === 'text' && (
                  <Input
                    id={`field-${field.key}`}
                    value={stringValue}
                    onChange={(e) => setField(field.key, e.target.value)}
                    placeholder={field.placeholder}
                    disabled={isSaving}
                  />
                )}

                {field.type === 'date' && (
                  <Input
                    id={`field-${field.key}`}
                    type="date"
                    value={stringValue}
                    onChange={(e) => setField(field.key, e.target.value)}
                    disabled={isSaving}
                  />
                )}

                {field.type === 'time' && (
                  <Input
                    id={`field-${field.key}`}
                    type="time"
                    value={stringValue}
                    onChange={(e) => setField(field.key, e.target.value)}
                    disabled={isSaving}
                  />
                )}

                {field.type === 'textarea' && (
                  <Textarea
                    id={`field-${field.key}`}
                    value={stringValue}
                    onChange={(e) => setField(field.key, e.target.value)}
                    placeholder={field.placeholder}
                    rows={3}
                    disabled={isSaving}
                  />
                )}

                {field.type === 'select' && field.options && (
                  <Select
                    value={stringValue}
                    onValueChange={(v) => setField(field.key, v)}
                    disabled={isSaving}
                  >
                    <SelectTrigger id={`field-${field.key}`}>
                      <SelectValue placeholder={field.placeholder ?? 'Select...'} />
                    </SelectTrigger>
                    <SelectContent>
                      {normalizeOptions(field.options).map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {field.type === 'owner' && (
                  <div className="h-9 flex items-center border border-input rounded-lg px-2 bg-card">
                    <OwnerPicker
                      value={stringValue || null}
                      teamMembers={teamMembers}
                      disabled={isSaving}
                      onChange={(userId) => setField(field.key, userId ?? '')}
                    />
                  </div>
                )}

                {field.type === 'checkbox' && (
                  <div className="flex items-center gap-2 h-9">
                    <Checkbox
                      id={`field-${field.key}`}
                      checked={boolValue}
                      onCheckedChange={(checked) => setField(field.key, checked === true)}
                      disabled={isSaving}
                    />
                    {field.placeholder && (
                      <label
                        htmlFor={`field-${field.key}`}
                        className="text-sm text-muted-foreground cursor-pointer"
                      >
                        {field.placeholder}
                      </label>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleSubmit()}
            disabled={!requiredOk || isSaving}
          >
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
